import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "flex items-center gap-2 min-w-0" }
const _hoisted_2 = {
  key: 0,
  class: "product-facts"
}
const _hoisted_3 = { class: "fact-values" }
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { class: "num" }
const _hoisted_6 = { key: 0 }
const _hoisted_7 = {
  key: 1,
  class: "info-row"
}
const _hoisted_8 = { class: "fact-values" }
const _hoisted_9 = ["href"]
const _hoisted_10 = {
  key: 1,
  class: "num"
}
const _hoisted_11 = { class: "num" }
const _hoisted_12 = {
  key: 2,
  class: "info-row"
}
const _hoisted_13 = { class: "support-badges" }
const _hoisted_14 = {
  key: 3,
  class: "info-row"
}
const _hoisted_15 = { class: "support-badges" }
const _hoisted_16 = {
  key: 1,
  class: "product-notes"
}
const _hoisted_17 = { class: "notes-content" }
const _hoisted_18 = {
  key: 2,
  class: "product-modules"
}
const _hoisted_19 = { class: "section-small-title" }
const _hoisted_20 = { class: "modules-list" }
const _hoisted_21 = {
  key: 0,
  class: "module-group-title"
}
const _hoisted_22 = ["title"]
const _hoisted_23 = { class: "module-name" }
const _hoisted_24 = ["title"]
const _hoisted_25 = { class: "module-hint" }
const _hoisted_26 = { key: 0 }
const _hoisted_27 = { key: 1 }
const _hoisted_28 = { class: "versions-header section-small-title" }
const _hoisted_29 = { class: "ui-field" }
const _hoisted_30 = { class: "versions-list" }
const _hoisted_31 = { class: "year-header" }
const _hoisted_32 = ["href"]
const _hoisted_33 = { key: 1 }
const _hoisted_34 = ["title"]

import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Hint } from '@/components/ui/hint';
import { Input } from '@/components/ui/input';
import { Package, Settings2, ChevronUp, ChevronDown, FileCode2 } from '@lucide/vue';

import { ref, computed, watch } from 'vue';
import moment from 'moment';
import PropertyProductSettingsDialog from '@/components/PropertyProductSettingsDialog.vue';

interface Props {
  propertyDetailProduct: PropertyDetailProduct;
}

const maxVersionsToShow = 5;

interface ModuleDisplaySection {
  heading: string;
  modules: PropertyModule[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PropertyProduct',
  props: {
    propertyDetailProduct: {}
  },
  emits: ['refresh'],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const product = ref<PropertyDetailProduct | null>(null);
const searchQuery = ref('');
const showAllVersions = ref(false);
const showVersionHistory = ref(false);
const showSettingsDialog = ref(false);
watch(
  () => props.propertyDetailProduct,
  (newVal) => {
    if (newVal) {
      product.value = JSON.parse(JSON.stringify(newVal));
    } else {
      product.value = null;
    }
  },
  { immediate: true, deep: true }
);

function formatDate(date: string): string {
  if (!date) return '';
  return moment(date).format('DD.MM.YYYY');
}

function moduleDateText(module: PropertyModule): string {
  if (module.license_expires) return formatDate(module.license_expires);
  if (module.acquired_at) return formatDate(module.acquired_at);
  return '';
}

function moduleDateTitle(module: PropertyModule): string | undefined {
  const parts: string[] = [];
  if (module.license_expires) parts.push(`Лицензия до ${formatDate(module.license_expires)}`);
  if (module.acquired_at) parts.push(`Приобретён ${formatDate(module.acquired_at)}`);
  return parts.length ? parts.join('. ') : undefined;
}

function formatDateTime(dateTime: string): string {
  if (!dateTime) return '';
  return moment(dateTime).format('DD.MM.YYYY HH:mm');
}

function isSupportIssue(): boolean {
  if (!product.value || !product.value?.info) return false;
  return product.value.info.support_suspended || isOverdueSupport(product.value.info.support_till);
}

function isOverdueSupport(date: any): boolean {
  return moment(date).diff(Date.now()) < 0;
}

function getSupportClass(): string {
  if (!product.value || !product.value?.info) return '';
  if (product.value.info.support_suspended) {
    return 'support-suspended';
  }
  if (isOverdueSupport(product.value.info.support_till)) {
    return 'support-overdue';
  }
  return '';
}

const filteredVersions = computed((): ProductVersion[] => {
  if (!product.value) return [];

  let versions = [...product.value.versions];

  // Фильтрация по поиску
  if (searchQuery.value && searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase();
    versions = versions.filter((v) => v.version.toLowerCase().includes(query));
  }

  // Сортировка по дате (новые сверху)
  versions.sort((a, b) => moment(b.created_at).valueOf() - moment(a.created_at).valueOf());

  // Ограничение количества, если не показываем все
  if (!showAllVersions.value && !searchQuery.value) {
    versions = versions.slice(0, maxVersionsToShow);
  }

  return versions;
});

const groupedVersions = computed((): Array<[string, ProductVersion[]]> => {
  const grouped: { [year: string]: ProductVersion[] } = {};

  filteredVersions.value.forEach((version) => {
    const year = moment(version.created_at).format('YYYY');
    if (!grouped[year]) {
      grouped[year] = [];
    }
    grouped[year].push(version);
  });

  // Сортируем года по убыванию (самые свежие сверху)
  return Object.entries(grouped).sort((a, b) => parseInt(b[0]) - parseInt(a[0]));
});

const canShowMore = computed((): boolean => {
  if (!product.value) return false;
  return product.value.versions.length > maxVersionsToShow && !searchQuery.value;
});

function toggleShowAll(): void {
  showAllVersions.value = !showAllVersions.value;
}

const latestVersion = computed((): ProductVersion | null => {
  if (!product.value || product.value.versions.length === 0) return null;

  // Sort versions by created_at and return the latest one
  const sortedVersions = [...product.value.versions].sort(
    (a, b) => moment(b.created_at).valueOf() - moment(a.created_at).valueOf()
  );

  return sortedVersions[0];
});

const moduleDisplaySections = computed((): ModuleDisplaySection[] => {
  if (!product.value?.modules?.length) return [];
  const list = product.value.modules;
  const withParent = list.filter((m) => m.module_parent_id != null);
  const orphans = list.filter((m) => m.module_parent_id == null);
  const groups = new Map<number, { heading: string; items: PropertyModule[] }>();
  for (const m of withParent) {
    const id = m.module_parent_id as number;
    if (!groups.has(id)) {
      groups.set(id, { heading: m.module_parent_name || `Группа ${id}`, items: [] });
    }
    groups.get(id)?.items.push(m);
  }
  const sections: ModuleDisplaySection[] = [...groups.values()]
    .map((g) => ({
      heading: g.heading,
      modules: [...g.items].sort((a, b) => a.module_name.localeCompare(b.module_name, 'ru')),
    }))
    .sort((a, b) => a.heading.localeCompare(b.heading, 'ru'));
  if (orphans.length) {
    sections.push({
      heading: orphans.length > 1 || sections.length > 0 ? 'Без группы' : '',
      modules: [...orphans].sort((a, b) => a.module_name.localeCompare(b.module_name, 'ru')),
    });
  }
  return sections.map((s) => ({
    heading: s.heading,
    modules: s.modules,
  }));
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (product.value)
    ? (_openBlock(), _createBlock(_unref(Card), {
        key: 0,
        class: "product-card"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(CardHeader), { class: "product-card-header" }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_1, [
                _createVNode(_unref(Package), {
                  size: 17,
                  class: "text-primary shrink-0"
                }),
                _createVNode(_unref(CardTitle), { class: "product-name" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(product.value.product), 1)
                  ]),
                  _: 1
                })
              ]),
              _createVNode(_unref(Button), {
                variant: "ghost",
                size: "icon",
                class: "ml-auto shrink-0",
                "aria-label": "Настройки продукта",
                title: "Настройки продукта",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (showSettingsDialog.value = true))
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Settings2), { size: 17 })
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_unref(CardContent), { class: "product-card-body" }, {
            default: _withCtx(() => [
              (product.value.info || latestVersion.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                    (product.value.info)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 0,
                          class: _normalizeClass(["info-row", getSupportClass()])
                        }, [
                          _cache[5] || (_cache[5] = _createElementVNode("span", { class: "info-label" }, "Поддержка", -1)),
                          _createElementVNode("span", _hoisted_3, [
                            (isSupportIssue())
                              ? (_openBlock(), _createBlock(_unref(Badge), {
                                  key: 0,
                                  variant: "destructive"
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(product.value.info.support_suspended ? 'Приостановлена' : 'Просрочена'), 1)
                                  ]),
                                  _: 1
                                }))
                              : (_openBlock(), _createElementBlock("span", _hoisted_4)),
                            _createElementVNode("span", _hoisted_5, _toDisplayString(formatDate(product.value.info.support_till)), 1)
                          ]),
                          _createVNode(_unref(Hint), { label: "Установка и поддержка" }, {
                            default: _withCtx(() => [
                              _createElementVNode("p", null, "Установка: " + _toDisplayString(product.value.info.installer), 1),
                              (product.value.createdDate)
                                ? (_openBlock(), _createElementBlock("p", _hoisted_6, "Дата установки: " + _toDisplayString(formatDate(product.value.createdDate)), 1))
                                : _createCommentVNode("", true),
                              _createElementVNode("p", null, "Поддержка: " + _toDisplayString(product.value.info.supporter), 1)
                            ]),
                            _: 1
                          })
                        ], 2))
                      : _createCommentVNode("", true),
                    (latestVersion.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                          _cache[6] || (_cache[6] = _createElementVNode("span", { class: "info-label" }, "Версия", -1)),
                          _createElementVNode("span", _hoisted_8, [
                            (latestVersion.value.url)
                              ? (_openBlock(), _createElementBlock("a", {
                                  key: 0,
                                  href: latestVersion.value.url,
                                  target: "_blank",
                                  rel: "noopener noreferrer",
                                  class: "info-link num"
                                }, _toDisplayString(latestVersion.value.version), 9, _hoisted_9))
                              : (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(latestVersion.value.version), 1)),
                            _createElementVNode("span", _hoisted_11, _toDisplayString(formatDate(latestVersion.value.created_at)), 1)
                          ]),
                          _createVNode(_unref(Button), {
                            variant: "ghost",
                            size: "icon",
                            class: "fact-action",
                            "aria-expanded": showVersionHistory.value,
                            "aria-label": "История версий",
                            title: "История версий",
                            onClick: _cache[1] || (_cache[1] = ($event: any) => (showVersionHistory.value = !showVersionHistory.value))
                          }, {
                            default: _withCtx(() => [
                              (showVersionHistory.value)
                                ? (_openBlock(), _createBlock(_unref(ChevronUp), {
                                    key: 0,
                                    size: 15
                                  }))
                                : (_openBlock(), _createBlock(_unref(ChevronDown), {
                                    key: 1,
                                    size: 15
                                  }))
                            ]),
                            _: 1
                          }, 8, ["aria-expanded"])
                        ]))
                      : _createCommentVNode("", true),
                    (product.value.info?.support_types?.length)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                          _cache[7] || (_cache[7] = _createElementVNode("span", { class: "info-label" }, "Тип:", -1)),
                          _createElementVNode("div", _hoisted_13, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(product.value.info.support_types, (supportType) => {
                              return (_openBlock(), _createBlock(_unref(Badge), {
                                key: supportType,
                                variant: "secondary"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(supportType), 1)
                                ]),
                                _: 2
                              }, 1024))
                            }), 128))
                          ]),
                          _cache[8] || (_cache[8] = _createElementVNode("span", null, null, -1))
                        ]))
                      : _createCommentVNode("", true),
                    (product.value.info && (product.value.info.support_24on7 || product.value.info.support_weekend))
                      ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                          _cache[11] || (_cache[11] = _createElementVNode("span", { class: "info-label" }, "Режим:", -1)),
                          _createElementVNode("div", _hoisted_15, [
                            (product.value.info.support_24on7)
                              ? (_openBlock(), _createBlock(_unref(Badge), {
                                  key: 0,
                                  variant: "outline"
                                }, {
                                  default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                                    _createTextVNode("24/7", -1)
                                  ]))]),
                                  _: 1
                                }))
                              : _createCommentVNode("", true),
                            (product.value.info.support_weekend)
                              ? (_openBlock(), _createBlock(_unref(Badge), {
                                  key: 1,
                                  variant: "outline"
                                }, {
                                  default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                                    _createTextVNode("выходные", -1)
                                  ]))]),
                                  _: 1
                                }))
                              : _createCommentVNode("", true)
                          ]),
                          _cache[12] || (_cache[12] = _createElementVNode("span", null, null, -1))
                        ]))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true),
              (product.value.info?.support_notes)
                ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                    _cache[13] || (_cache[13] = _createElementVNode("h3", { class: "section-small-title" }, "Заметки", -1)),
                    _createElementVNode("p", _hoisted_17, _toDisplayString(product.value.info.support_notes), 1)
                  ]))
                : _createCommentVNode("", true),
              (product.value.modules?.length)
                ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                    _createElementVNode("h3", _hoisted_19, [
                      _cache[14] || (_cache[14] = _createTextVNode(" Модули ", -1)),
                      _createVNode(_unref(Badge), { variant: "outline" }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(product.value.modules.length), 1)
                        ]),
                        _: 1
                      })
                    ]),
                    _createElementVNode("div", _hoisted_20, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(moduleDisplaySections.value, (section, secIdx) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: `mod-sec-${secIdx}`
                        }, [
                          (section.heading)
                            ? (_openBlock(), _createElementBlock("h4", _hoisted_21, _toDisplayString(section.heading), 1))
                            : _createCommentVNode("", true),
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(section.modules, (module) => {
                            return (_openBlock(), _createElementBlock("div", {
                              key: module.id,
                              class: "module-row"
                            }, [
                              _createElementVNode("span", {
                                class: _normalizeClass(["mark", { 'mark-paid': module.is_paid }]),
                                title: module.is_paid ? 'Платный' : undefined
                              }, [
                                (module.is_paid)
                                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                      _createTextVNode("₽")
                                    ], 64))
                                  : _createCommentVNode("", true)
                              ], 10, _hoisted_22),
                              _createElementVNode("span", _hoisted_23, _toDisplayString(module.module_name), 1),
                              _createElementVNode("span", {
                                class: "num module-dates",
                                title: moduleDateTitle(module)
                              }, _toDisplayString(moduleDateText(module)), 9, _hoisted_24),
                              _createElementVNode("span", _hoisted_25, [
                                (module.module_description || module.notes)
                                  ? (_openBlock(), _createBlock(_unref(Hint), {
                                      key: 0,
                                      label: module.notes ? 'Примечание' : 'Описание'
                                    }, {
                                      default: _withCtx(() => [
                                        (module.module_description)
                                          ? (_openBlock(), _createElementBlock("p", _hoisted_26, _toDisplayString(module.module_description), 1))
                                          : _createCommentVNode("", true),
                                        (module.notes)
                                          ? (_openBlock(), _createElementBlock("p", _hoisted_27, _toDisplayString(module.notes), 1))
                                          : _createCommentVNode("", true)
                                      ]),
                                      _: 2
                                    }, 1032, ["label"]))
                                  : _createCommentVNode("", true)
                              ])
                            ]))
                          }), 128))
                        ]))
                      }), 128))
                    ])
                  ]))
                : _createCommentVNode("", true),
              (product.value.versions.length && showVersionHistory.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                    _createElementVNode("div", _hoisted_28, [
                      _cache[15] || (_cache[15] = _createTextVNode(" История версий ", -1)),
                      _createVNode(_unref(Badge), { variant: "outline" }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(product.value.versions.length), 1)
                        ]),
                        _: 1
                      })
                    ]),
                    _createElementVNode("label", _hoisted_29, [
                      _cache[16] || (_cache[16] = _createElementVNode("span", { class: "ui-label" }, "Поиск версии", -1)),
                      _createVNode(_unref(Input), {
                        modelValue: searchQuery.value,
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((searchQuery).value = $event)),
                        type: "search",
                        placeholder: "Номер версии"
                      }, null, 8, ["modelValue"])
                    ]),
                    _createElementVNode("div", _hoisted_30, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groupedVersions.value, ([year, group]) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: `year-${year}`
                        }, [
                          _createElementVNode("div", _hoisted_31, [
                            _createElementVNode("span", null, _toDisplayString(year), 1),
                            _createVNode(_unref(Badge), { variant: "outline" }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(group.length), 1)
                              ]),
                              _: 2
                            }, 1024)
                          ]),
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group, (version) => {
                            return (_openBlock(), _createElementBlock("div", {
                              key: version.version,
                              class: "version-row"
                            }, [
                              (version.url)
                                ? (_openBlock(), _createElementBlock("a", {
                                    key: 0,
                                    href: version.url,
                                    target: "_blank",
                                    rel: "noopener noreferrer",
                                    class: "version-link"
                                  }, _toDisplayString(version.version), 9, _hoisted_32))
                                : (_openBlock(), _createElementBlock("span", _hoisted_33, _toDisplayString(version.version), 1)),
                              _createElementVNode("time", {
                                class: "num version-date",
                                title: formatDateTime(version.created_at)
                              }, _toDisplayString(formatDate(version.created_at)), 9, _hoisted_34)
                            ]))
                          }), 128))
                        ]))
                      }), 128))
                    ]),
                    (canShowMore.value)
                      ? (_openBlock(), _createBlock(_unref(Button), {
                          key: 0,
                          variant: "ghost",
                          size: "sm",
                          onClick: toggleShowAll
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(showAllVersions.value ? 'Скрыть' : `Показать все (${product.value.versions.length})`), 1)
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true)
                  ], 64))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }),
          (product.value.versions.length)
            ? (_openBlock(), _createBlock(_unref(CardFooter), {
                key: 0,
                class: "product-footer"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Button), {
                    "as-child": "",
                    variant: "ghost",
                    size: "sm"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_router_link, {
                        to: {
            name: 'text-info-changes',
            params: { propertyId: product.value.propertyId, product: product.value.product },
          }
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(FileCode2), { size: 14 }),
                          _cache[17] || (_cache[17] = _createTextVNode(" Изменения параметров ", -1))
                        ]),
                        _: 1
                      }, 8, ["to"])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (product.value)
            ? (_openBlock(), _createBlock(PropertyProductSettingsDialog, {
                key: 1,
                modelValue: showSettingsDialog.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((showSettingsDialog).value = $event)),
                "property-id": product.value.propertyId,
                "product-id": product.value.productId,
                "product-name": product.value.product,
                "current-modules": product.value.modules,
                "support-info": product.value.info,
                onSaved: _cache[4] || (_cache[4] = ($event: any) => (emit('refresh')))
              }, null, 8, ["modelValue", "property-id", "product-id", "product-name", "current-modules", "support-info"]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }))
    : _createCommentVNode("", true)
}
}

})